import * as Sentry from "@sentry/browser";
import type { User as SentryUser } from "@sentry/browser";
import { fetchLatestBuildVersion } from "./buildVersion";
import { getSessionUser } from "./users/auth";
import type { User } from "./users/model";
import Configuration from "../config";
import { getBrowserTabId } from "../utils/storage";
import { getCurrentServerTime, getServerTimeOffset } from "utils/date";
import { LocalStorageKeys, SessionStorageKeys } from "utils/storageKeys";

export const LOG_LEVEL = {
  INFO: "info",
  WARNING: "warning",
  ERROR: "error",
  DEBUG: "debug",
} as const;

type LogLevel = (typeof LOG_LEVEL)[keyof typeof LOG_LEVEL];

type NetworkInfo = {
  downlink?: number;
  downlinkMax?: number;
  effectiveType?: string;
  rtt?: number;
  type?: string;
};

type UserLoggingContext = SentryUser & {
  id: string;
  email?: string;
  first_name: string;
  last_name: string | null;
  current_time: string;
  current_server_time: Date;
  utc_offset: string;
  time_offset?: string;
  network_info: NetworkInfo;
};

const LOGGER_URL = "https://bc6c71e6c0e64fe19da19315c10aa826@sentry.io/283084";
const DEFAULT_LEVEL: LogLevel = LOG_LEVEL.INFO;
const LESSON_TAG_NAME = "lesson.id";
let consoleLoggingEnabled = false;
let loggerConfigured = false;

export async function configLogger() {
  consoleLoggingEnabled = Boolean(
    Configuration.ENABLE_CONSOLE_LOGGING ||
      localStorage.getItem(LocalStorageKeys.enableConsoleLogging)
  );

  if (!Configuration.ENABLE_REMOTE_LOGGING) {
    return;
  }

  let release: string;
  try {
    release = await fetchLatestBuildVersion();
  } catch {
    release = "not available";
  }

  Sentry.init({ dsn: LOGGER_URL, release });
  loggerConfigured = true;
}

type NavigatorWithConnection = Navigator & {
  connection?: NetworkInfo;
};

function getNetworkInfo(): NetworkInfo {
  const networkInfo = (navigator as NavigatorWithConnection).connection;
  if (!networkInfo) {
    return {};
  }

  return {
    downlink: networkInfo.downlink,
    downlinkMax: networkInfo.downlinkMax,
    effectiveType: networkInfo.effectiveType,
    rtt: networkInfo.rtt,
    type: networkInfo.type,
  };
}

function buildUserLoggingContext(user: User): UserLoggingContext {
  const context: UserLoggingContext = {
    id: user.id,
    email: user.profile.email ?? undefined,
    first_name: user.profile.first_name,
    last_name: user.profile.last_name,
    current_time: new Date().toString(),
    current_server_time: getCurrentServerTime().toDate(),
    utc_offset: user.profile.timezone ? user.profile.timezone.utc_offset : "",
    network_info: getNetworkInfo(),
  };

  const timeOffset = getServerTimeOffset();
  if (timeOffset !== null) {
    context.time_offset = String(timeOffset);
  }

  return context;
}

export function updateUserLoggingContext(user: User) {
  if (!loggerConfigured) {
    return;
  }

  Sentry.setUser(buildUserLoggingContext(user));
}

export function clearUserContext() {
  if (!loggerConfigured) {
    return;
  }

  Sentry.setUser(null);
}

function stringifyMessage(message: unknown) {
  return String(message);
}

function logToConsole(method: "log" | "warn" | "error" | "debug", ...args: unknown[]) {
  if (!consoleLoggingEnabled) {
    return;
  }

  // eslint-disable-next-line no-console
  console[method](...args);
}

export function log(message: unknown, data?: unknown, level: LogLevel = DEFAULT_LEVEL) {
  if (!loggerConfigured) {
    return;
  }

  syncBrowserTabIdTag();

  let formattedMessage = stringifyMessage(message);
  const user = getSessionUser();
  if (user) {
    updateUserLoggingContext(user);
    const userMessageIdentifier = user.profile.email ?? user.id;
    formattedMessage = `${userMessageIdentifier}: ${formattedMessage}`;
  }

  formattedMessage += ` (${getCurrentServerTime().toJSON()})`;

  Sentry.captureMessage(formattedMessage, {
    level,
    extra: toExtras(data),
  });
}

// Sentry types extras as a keyed record; raven accepted any value, so keep
// object payloads intact and wrap primitives to preserve them in the event.
function toExtras(data: unknown): Record<string, unknown> {
  if (data === null || data === undefined) {
    return {};
  }

  if (typeof data === "object") {
    return data as Record<string, unknown>;
  }

  return { data };
}

export function logInfo(message: unknown, data?: unknown) {
  log(message, data, LOG_LEVEL.INFO);
  logToConsole("log", message, data);
}

export function logError(message: unknown, data?: unknown) {
  log(message, data, LOG_LEVEL.ERROR);
  logToConsole("error", message, data);
}

export function logWarning(message: unknown, data?: unknown) {
  log(message, data, LOG_LEVEL.WARNING);
  logToConsole("warn", message, data);
}

export function logDebug(...args: unknown[]) {
  logToConsole("debug", ...args);
}

export function setLessonTag(id: string | number) {
  if (!loggerConfigured) {
    return;
  }

  Sentry.setTag(LESSON_TAG_NAME, String(id));
}

export function removeLessonTag() {
  if (!loggerConfigured) {
    return;
  }

  // Undefined-valued tags are dropped during event serialization, which is
  // Sentry's documented way to remove a tag from the scope.
  Sentry.setTag(LESSON_TAG_NAME, undefined);
}

function syncBrowserTabIdTag() {
  const browserTabId = getBrowserTabId();
  if (!browserTabId) {
    return;
  }

  Sentry.setTag(SessionStorageKeys.tabId, browserTabId);
}
